import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { ProductRegistrationFormInput } from 'models/productRegistration';
import * as routes from 'router/routes';
import { useQuery } from 'hooks/useQuery';
import { useTranslations } from 'hooks/useTranslations';
import { useLazyValidateProductIdentifiersQuery } from 'store/productService';
import {
  getIdentifierValidationStatus,
  isProductRegistered,
  isProductIdentifierValid
} from 'utils/identifierValidationUtils';
import { howToFindProductInfoUrl } from 'utils/constants';
import { RegistrationQrCode } from 'utils/productQrUtils';
import { removeSpecialChars } from 'utils/stringUtils';
import { pushGTMEvent } from 'utils/analyticsUtils';
import labelIconPath from 'assets/images/labelicon.png';
import qrCode from 'assets/vectors/qr.svg';

import SeoHelmet from 'components/fragments/SeoHelmet';
import BarcodeScanner from 'components/modals/BarcodeScanner';
import InputField from 'components/UI/InputField';
import Button from 'components/UI/Buttons/Button';
import PageWrapper from 'components/UI/Page/PageWrapper';
import PageHeader from 'components/UI/Page/PageHeader';
import Copyright from 'components/UI/Copyright';

import { Link } from 'styles';
import { ImageContainer, Image, Form, Divider } from './styled';

// Zod schema
const schema = z
  .object({
    husqvarnaId: z.string().nullable(),
    pnc: z.string().nullable(),
    serialNumber: z.string().nullable()
  })
  .superRefine((data, ctx) => {
    const hasHid = !!data.husqvarnaId;
    const hasPncSerial = !!data.pnc || !!data.serialNumber;

    if (!hasHid && !hasPncSerial) {
      ctx.addIssue({
        path: ['husqvarnaId'],
        code: 'custom',
        message: 'productregistration.form.husqvarnaId_required'
      });
    }
    if (!data.pnc && !hasHid) {
      ctx.addIssue({
        path: ['pnc'],
        code: 'custom',
        message: 'productregistration.form.pnc_required'
      });
    }
    if (!data.serialNumber && !hasHid) {
      ctx.addIssue({
        path: ['serialNumber'],
        code: 'custom',
        message: 'productregistration.form.serial_number_required'
      });
    }
  });

const ProductRegistrationPage: FC = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannedIdentifiers, setScannedIdentifiers] =
    useState<RegistrationQrCode | null>(null);
  const [validateProductIdentifiers, { isFetching: isValidatingIdentifiers }] =
    useLazyValidateProductIdentifiersQuery();

  // Params
  const [pnc, serialNumber, husqvarnaId] = useQuery(
    'pnc',
    'serialNumber',
    'husqvarnaId'
  );

  // Form
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    resetField,
    setError
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      pnc: pnc ?? null,
      serialNumber: serialNumber ?? null,
      husqvarnaId: husqvarnaId ?? null
    },
    resolver: zodResolver(schema)
  });

  // Watch input fields - using useWatch for React 19 compiler compatibility
  const [pncInput, serialNumberInput, husqvarnaIdInput] = useWatch({
    control,
    name: ['pnc', 'serialNumber', 'husqvarnaId']
  });

  // Apply a scan to either the Husqvarna ID or the PNC and serial fields.
  useEffect(() => {
    if (!scannedIdentifiers) {
      return;
    }

    if (scannedIdentifiers.type === 'hid') {
      reset({
        husqvarnaId: scannedIdentifiers.husqvarnaId,
        pnc: null,
        serialNumber: null
      });
      return;
    }

    reset({
      husqvarnaId: null,
      pnc: scannedIdentifiers.pnc,
      serialNumber: scannedIdentifiers.serialNumber
    });
  }, [scannedIdentifiers, reset]);

  // Submit
  const onSubmit: SubmitHandler<ProductRegistrationFormInput> = useCallback(
    async (values) => {
      pushGTMEvent('productRegistrationLookup');
      const qPnc = removeSpecialChars(values.pnc);
      const qSerial = removeSpecialChars(values.serialNumber);
      const qHid = removeSpecialChars(values.husqvarnaId);

      // Validate Husqvarna Id
      if (qHid && qHid.length !== 25) {
        return setError('husqvarnaId', {
          message: 'productregistration.form.husqvarnaId_invalid_length'
        });
      }

      // Validate PNC
      if (qPnc && qPnc.length !== 9) {
        return setError('pnc', {
          message: 'productregistration.form.pnc_invalid_length'
        });
      }

      const validationResult = await validateProductIdentifiers({
        husqvarnaId: qHid,
        pnc: qPnc,
        serialNumber: qSerial
      });

      if ('error' in validationResult) {
        return;
      }

      if (!validationResult.data) {
        return;
      }

      const productIsRegistered = isProductRegistered(validationResult.data);
      const invalidProfiles = productIsRegistered
        ? null
        : isProductIdentifierValid(validationResult.data);

      if (invalidProfiles) {
        const { hasInvalidPnc, hasInvalidHid, hasInvalidSerialNumber } =
          getIdentifierValidationStatus(invalidProfiles);

        if (hasInvalidSerialNumber) {
          setError('serialNumber', {
            message: 'productregistration.form.serial_number_invalid'
          });
        }

        if (hasInvalidPnc) {
          setError('pnc', {
            message: 'productregistration.form.pnc_invalid'
          });
        }

        if (hasInvalidHid) {
          setError('husqvarnaId', {
            message: 'productregistration.form.husqvarnaId_invalid'
          });
        }

        return;
      }

      const params = qHid
        ? `husqvarnaId=${qHid}`
        : `productno=${qPnc}&serialNumber=${qSerial}`;

      navigate(`${routes.PRODUCT_INFO}?${params}`);
    },
    [navigate, setError, validateProductIdentifiers]
  );

  // Open scanner
  const onOpenScanner = useCallback(() => {
    pushGTMEvent('scanQRCodeClick');
    setScannerOpen(true);
  }, []);
  const onCloseScanner = useCallback(() => setScannerOpen(false), []);

  return (
    <PageWrapper responsive={false}>
      <Copyright>
        <SeoHelmet title="productregistration.meta_title" />
        {scannerOpen && (
          <BarcodeScanner
            onClose={onCloseScanner}
            scanResult={setScannedIdentifiers}
          />
        )}
        <ImageContainer>
          <Image src={labelIconPath} alt="product registration" />
        </ImageContainer>
        <PageHeader
          title={t('productregistration.title')}
          subtitle={t('productregistration.subtitle')}
        />
        <Link
          href={howToFindProductInfoUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('productregistration.how_to_find_link')}
        </Link>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputField
            name="husqvarnaId"
            description="productregistration.form.husqvarnaId"
            error={errors.husqvarnaId}
            register={register('husqvarnaId')}
            icon={{
              src: qrCode,
              alt: 'qr',
              onClick: onOpenScanner
            }}
            disabled={!!(pncInput || serialNumberInput)}
            reset={resetField}
          />
          <Divider>
            <div />
            <p>{t('productregistration.form.or')}</p>
            <div />
          </Divider>
          <InputField
            name="pnc"
            description="productregistration.form.pnc"
            error={errors.pnc}
            register={register('pnc')}
            disabled={!!husqvarnaIdInput}
            reset={resetField}
            helperMessage={'productregistration.form.pnc_helper_message'}
          />
          <InputField
            name="serialNumber"
            description="productregistration.form.serial_number"
            error={errors.serialNumber}
            register={register('serialNumber')}
            disabled={!!husqvarnaIdInput}
            reset={resetField}
          />
          <Button submit fullWidth disabled={isValidatingIdentifiers}>
            {t('productregistration.form.btn_submit')}
          </Button>
        </Form>
      </Copyright>
    </PageWrapper>
  );
};

export default ProductRegistrationPage;
